import axios from "axios";
import { alertFailed } from "@/functions/swAlert";
import { msalInstance, loginRequest } from "@/authConfig";
import { createFetch } from "@vueuse/core";
import { 
  handleMockResponse,
  MockConfigManager,
  MockLogger,
  FORNITORI_ENDPOINTS
} from "@/mocks/api";

export const baseUrlApiApp = process.env.VUE_APP_ENDPOINT_API;

// Log configurazione mock all'avvio
const mockConfig = MockConfigManager.getConfig();
if (mockConfig.enabled) {
  MockLogger.log(
    "Sistema mock abilitato", 
    { 
      delay: mockConfig.delay,
      randomErrors: mockConfig.randomErrors 
    }
  );
}

// ============== UTILITY JWT ==============
/**
 * Decodifica un JWT e restituisce il payload
 */
function decodeJWT(token: string): any {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Errore decodifica JWT:', error);
    return null;
  }
}

/**
 * Verifica se un token JWT è scaduto o sta per scadere
 * @param token - Il token JWT da verificare
 * @param bufferSeconds - Secondi di buffer prima della scadenza (default: 300 = 5 minuti)
 * @returns true se il token è scaduto o sta per scadere
 */
function isTokenExpired(token: string, bufferSeconds = 300): boolean {
  if (!token) return true;
  
  const payload = decodeJWT(token);
  if (!payload || !payload.exp) return true;
  
  const now = Math.floor(Date.now() / 1000);
  const expirationWithBuffer = payload.exp - bufferSeconds;
  
  return now >= expirationWithBuffer;
}

/**
 * Flag per evitare refresh multipli simultanei
 */
let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

function subscribeTokenRefresh(callback: (token: string) => void) {
  refreshSubscribers.push(callback);
}

function onTokenRefreshed(token: string) {
  refreshSubscribers.forEach(callback => callback(token));
  refreshSubscribers = [];
}

/**
 * Rinnova il token usando MSAL
 */
async function refreshToken(): Promise<string> {
  try {
    const response = await msalInstance.acquireTokenSilent(loginRequest);
    const newToken = response.idToken;
    localStorage.setItem("tokenAdb2c", newToken);
    console.log('✅ Token rinnovato con successo');
    return newToken;
  } catch (error) {
    console.error('❌ Errore nel refresh del token:', error);
    // Se il refresh fallisce, forza il logout
    localStorage.clear();
    msalInstance.logoutRedirect();
    throw error;
  }
}

export const dbManagApp = axios.create({
  baseURL: baseUrlApiApp,
});

// ============== REQUEST INTERCEPTOR ==============
// Verifica e rinnova il token prima di ogni richiesta
dbManagApp.interceptors.request.use(
  async (config) => {
    let token = localStorage.getItem("tokenAdb2c");
    
    // Verifica se il token è scaduto o sta per scadere
    if (token && isTokenExpired(token)) {
      console.warn('⚠️ Token scaduto o in scadenza, rinnovo in corso...');
      
      if (!isRefreshing) {
        isRefreshing = true;
        try {
          token = await refreshToken();
          isRefreshing = false;
          onTokenRefreshed(token);
        } catch (error) {
          isRefreshing = false;
          throw error;
        }
      } else {
        // Se un refresh è già in corso, aspetta che finisca
        token = await new Promise<string>((resolve) => {
          subscribeTokenRefresh((newToken: string) => {
            resolve(newToken);
          });
        });
      }
    }
    
    if (token) {
      config.headers["Authorization"] = "Bearer " + token;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// ============== RESPONSE INTERCEPTOR ==============
// Gestisce errori 401 con retry automatico dopo refresh del token
dbManagApp.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // Se riceviamo 401 e non abbiamo già ritentato
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      console.warn('⚠️ Ricevuto 401, tentativo di refresh token...');
      
      if (!isRefreshing) {
        isRefreshing = true;
        try {
          const newToken = await refreshToken();
          isRefreshing = false;
          onTokenRefreshed(newToken);
          
          // Riprova la richiesta originale con il nuovo token
          originalRequest.headers["Authorization"] = "Bearer " + newToken;
          return dbManagApp(originalRequest);
        } catch (refreshError) {
          isRefreshing = false;
          return Promise.reject(refreshError);
        }
      } else {
        // Se un refresh è già in corso, aspetta e riprova
        return new Promise((resolve, reject) => {
          subscribeTokenRefresh((newToken: string) => {
            originalRequest.headers["Authorization"] = "Bearer " + newToken;
            resolve(dbManagApp(originalRequest));
          });
        });
      }
    }
    
    return Promise.reject(error);
  }
);

export const requestAppPost = async (
  endpoint: string,
  payload: Record<string, unknown>,
  noAlert?: boolean
) => {
  // Controlla se abbiamo una risposta mock
  const mockResult = await handleMockResponse(endpoint, "POST", payload);
  if (mockResult) return mockResult;

  return dbManagApp
    .post(endpoint, payload)
    .then((res) => {
      if (res.status == 401) {
        // msalInstance.logoutRedirect();
        throw new Error("401");
      }

      return {
        ...res,
      };
    })
    .catch((e) => {
      if (e == "401") {
        alertFailed(
          "Sessione Scaduta. Per continuare, effettuare nuovamente il login"
        ).then(() => msalInstance.logoutRedirect());
      }
      if (!noAlert) {
        alertFailed(
          e.response.data.message
            ? e.response.data.message
            : "Attenzione! Si è verificato un errore. Riprovare più tardi"
        );
      }

      if (e.response.data.message == "Utente non autorizzato") {
        msalInstance.logoutRedirect();
      }

      return {
        ...e.response,
      };
    });
};

export const requestAppPostNoAlert = async (
  endpoint: string,
  payload: Record<string, unknown>
) => {
  // Controlla se abbiamo una risposta mock
  const mockResult = await handleMockResponse(endpoint, "POST", payload);
  if (mockResult) return mockResult;

  return dbManagApp
    .post(endpoint, payload)
    .then((res) => {
      if (res.status == 401) {
        // msalInstance.logoutRedirect();
        throw new Error("401");
      }
      return {
        ...res,
      };
    })
    .catch((e) => {
      if (e == "401") {
        alertFailed(
          "Sessione Scaduta. Per continuare, effettuare nuovamente il login"
        ).then(() => msalInstance.logoutRedirect());
      }
      return {
        ...e.response,
      };
    });
};

let controller;

export const abortRequest = () => {
  controller.abort("Ricerca utente");
};

export const requestAppGet = async (
  endpoint: string,
  params: Record<string, unknown>
) => {
  console.log("requestAppGet", endpoint, params);

  // Controlla se abbiamo una risposta mock
  const mockResult = await handleMockResponse(endpoint, "GET");
  if (mockResult) return mockResult;

  return dbManagApp
    .get(endpoint, params)
    .then((res) => {
      if (res.status == 401) {
        // msalInstance.logoutRedirect();
        throw new Error("401");
      }
      return {
        ...res,
      };
    })
    .catch((e) => {
      if (e == "401") {
        alertFailed(
          "Sessione Scaduta. Per continuare, effettuare nuovamente il login"
        ).then(() => msalInstance.logoutRedirect());
      } else {
        alertFailed(
          e.response.data.message
            ? e.response.data.message
            : "Attenzione! Si è verificato un errore. Riprovare più tardi"
        );
      }

      return {
        ...e.response,
      };
    });
};

export const requestAppGetNoAlert = async (
  endpoint: string,
  params: Record<string, unknown>
) => {
  // Controlla se abbiamo una risposta mock
  const mockResult = await handleMockResponse(endpoint, "GET");
  if (mockResult) return mockResult;

  return dbManagApp
    .get(endpoint, params)
    .then((res) => {
      if (res.status == 401) {
        // msalInstance.logoutRedirect();
        throw new Error("401");
      }
      return {
        ...res,
      };
    })
    .catch((e) => {
      if (e == "401") {
        alertFailed(
          "Sessione Scaduta. Per continuare, effettuare nuovamente il login"
        ).then(() => msalInstance.logoutRedirect());
      }
      return {
        ...e.response,
      };
    });
};

export const sendFile = axios.create({
  baseURL: `${baseUrlApiApp}`,
  // data: bodyFormData,
  // headers: { "Content-Type": "multipart/form-data" }, // Lasciamo che sia il browser a settare il content-type corretto con il boundary
  onUploadProgress: function (progressEvent) {
    console.log("cambio percentuale upload file", progressEvent);
    // let percentCompleted = Math.round(
    //   (progressEvent.loaded * 100) / progressEvent.total
    // );
  },

  /* other custom settings */
});
sendFile.interceptors.request.use((config) => {
  const token = localStorage.getItem("tokenAdb2c");
  if (localStorage.getItem("tokenAdb2c")) {
    config.headers["Authorization"] = "Bearer " + token;
  }
  return config;
});
export const fileSender = async (
  apiLink,
  bodyFormData,
  method = "POST",
  headers = {}
) => {
  return sendFile({
    method: method as any,
    url: apiLink,
    data: bodyFormData,
    headers: headers,
  })
    .then((res) => {
      if (res.status == 401) {
        // msalInstance.logoutRedirect();
        throw new Error("401");
      }
      return {
        ...res,
      };
    })
    .catch((e) => {
      if (e == "401") {
        alertFailed(
          "Sessione Scaduta. Per continuare, effettuare nuovamente il login"
        ).then(() => msalInstance.logoutRedirect());
      } else {
        alertFailed(
          e && e.response && e.response.data && e.response.data.message
            ? e.response.data.message
            : "Attenzione! Si è verificato un errore. Riprovare più tardi"
        );
      }
      return {
        ...e.response,
      };
    });
};

const dbManagControlloTokenFCH = axios.create({
  baseURL: baseUrlApiApp,
  // headers: {
  //   Authorization: "Bearer " + localStorage.getItem("id_token"),
  // },
  /* other custom settings */
});

// Intercettiamo fetch globalmente per gestire token e mock
const originalFetch = window.fetch;
window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
  const config = MockConfigManager.getConfig();
  
  // Verifica se è una chiamata che dovrebbe essere mockable
  let url = "";
  if (typeof input === "string") {
    url = input;
  } else if (input instanceof URL) {
    url = input.toString();
  } else if (input instanceof Request) {
    url = input.url;
  }

  MockLogger.log(
    `URL: ${url}, baseUrlApiApp: ${baseUrlApiApp}, enabled: ${config.enabled}`
  );

  // ============== GESTIONE TOKEN PER FETCH ==============
  // Verifica e rinnova il token se necessario (come nell'interceptor axios)
  if (baseUrlApiApp && url.startsWith(baseUrlApiApp)) {
    let token = localStorage.getItem("tokenAdb2c");
    
    if (token && isTokenExpired(token)) {
      console.warn('⚠️ [FETCH] Token scaduto o in scadenza, rinnovo in corso...');
      
      if (!isRefreshing) {
        isRefreshing = true;
        try {
          token = await refreshToken();
          isRefreshing = false;
          onTokenRefreshed(token);
        } catch (error) {
          isRefreshing = false;
          throw error;
        }
      } else {
        // Se un refresh è già in corso, aspetta
        token = await new Promise<string>((resolve) => {
          subscribeTokenRefresh((newToken: string) => {
            resolve(newToken);
          });
        });
      }
    }
    
    // Aggiorna l'header Authorization con il token (nuovo o esistente)
    if (token) {
      init = init || {};
      init.headers = {
        ...init.headers,
        "Authorization": `Bearer ${token}`,
      };
    }
  }

  // Se è una chiamata alla nostra API e i mock sono abilitati
  if (config.enabled && baseUrlApiApp && url.startsWith(baseUrlApiApp)) {
    let endpoint = url.replace(baseUrlApiApp, "");
    // Assicurati che l'endpoint inizi con /
    if (!endpoint.startsWith("/")) {
      endpoint = "/" + endpoint;
    }
    const method = (init?.method || "GET").toUpperCase();
    
    let payload: Record<string, unknown> | undefined;
    if (init?.body && typeof init.body === 'string') {
      try {
        payload = JSON.parse(init.body);
      } catch (e) {
        // Ignore JSON parse errors
      }
    }
    
    const mockResponse = await handleMockResponse(endpoint, method, payload);

    if (mockResponse) {
      MockLogger.log(`FETCH ${method} ${endpoint}`, mockResponse);

      // Crea una Response mock
      return new Response(JSON.stringify(mockResponse.data), {
        status: mockResponse.status,
        statusText: mockResponse.status >= 400 ? "Error" : "OK",
        headers: {
          "Content-Type": "application/json",
          ...(mockResponse.headers || {}),
        },
      });
    } else {
      MockLogger.log(`No mock found for ${method} ${endpoint}`);
    }
  }

  // Esegui la fetch reale
  MockLogger.log(`Proceeding with real fetch for: ${url}`);
  const response = await originalFetch(input, init);
  
  // ============== GESTIONE 401 CON RETRY ==============
  // Se riceviamo 401, prova a refreshare il token e riprova
  if (response.status === 401 && baseUrlApiApp && url.startsWith(baseUrlApiApp)) {
    console.warn('⚠️ [FETCH] Ricevuto 401, tentativo di refresh token...');
    
    if (!isRefreshing) {
      isRefreshing = true;
      try {
        const newToken = await refreshToken();
        isRefreshing = false;
        onTokenRefreshed(newToken);
        
        // Riprova la chiamata con il nuovo token
        const newInit = {
          ...init,
          headers: {
            ...init?.headers,
            "Authorization": `Bearer ${newToken}`,
          },
        };
        return originalFetch(input, newInit);
      } catch (error) {
        isRefreshing = false;
        return response; // Ritorna la risposta 401 originale
      }
    }
  }
  
  return response;
};

export const useFetchJSON = createFetch({
  baseUrl: baseUrlApiApp,
  combination: "overwrite",
  options: {
    immediate: false,
    async beforeFetch({ options }) {
      console.log("Base Url Api: ", baseUrlApiApp);
      
      // Verifica e rinnova il token prima della fetch
      let token = localStorage.getItem("tokenAdb2c");
      
      if (token && isTokenExpired(token)) {
        console.warn('⚠️ [useFetchJSON] Token scaduto, rinnovo prima della chiamata...');
        
        if (!isRefreshing) {
          isRefreshing = true;
          try {
            token = await refreshToken();
            isRefreshing = false;
            onTokenRefreshed(token);
          } catch (error) {
            isRefreshing = false;
            throw error;
          }
        } else {
          token = await new Promise<string>((resolve) => {
            subscribeTokenRefresh((newToken: string) => {
              resolve(newToken);
            });
          });
        }
      }
      
      options.headers = {
        ...options.headers,
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      };
      return { options };
    },
    updateDataOnError: true, // IMPORTANTE: permette di aggiornare data anche in caso di errore
    async onFetchError(ctx) {
      // Cattura il body della risposta di errore e lo mette in data
      console.log("useFetchJSON onFetchError - ctx:", ctx);
      if (ctx.response) {
        try {
          // Clona la response per poterla leggere
          const errorData = await ctx.response.clone().json();
          console.log("useFetchJSON - Parsed error body:", errorData);
          // Metti i dati dell'errore in ctx.data così vengono passati al componente
          ctx.data = errorData;
        } catch (e) {
          console.error("useFetchJSON - Errore parsing error body:", e);
        }
      }
      return ctx;
    },
  },
});

// Funzioni helper per PUT e DELETE con supporto mock
export const requestAppPut = async (
  endpoint: string,
  payload: Record<string, unknown>,
  noAlert?: boolean
) => {
  // Controlla se abbiamo una risposta mock
  const mockResult = await handleMockResponse(endpoint, "PUT", payload);
  if (mockResult) return mockResult;

  return dbManagApp
    .put(endpoint, payload)
    .then((res) => {
      if (res.status == 401) {
        throw new Error("401");
      }
      return { ...res };
    })
    .catch((e) => {
      if (e == "401") {
        alertFailed(
          "Sessione Scaduta. Per continuare, effettuare nuovamente il login"
        ).then(() => msalInstance.logoutRedirect());
      }
      if (!noAlert) {
        alertFailed(
          e.response?.data?.message ||
            "Attenzione! Si è verificato un errore. Riprovare più tardi"
        );
      }
      return { ...e.response };
    });
};

export const requestAppDelete = async (endpoint: string, noAlert?: boolean) => {
  // Controlla se abbiamo una risposta mock
  const mockResult = await handleMockResponse(endpoint, "DELETE");
  if (mockResult) return mockResult;

  return dbManagApp
    .delete(endpoint)
    .then((res) => {
      if (res.status == 401) {
        throw new Error("401");
      }
      return { ...res };
    })
    .catch((e) => {
      if (e == "401") {
        alertFailed(
          "Sessione Scaduta. Per continuare, effettuare nuovamente il login"
        ).then(() => msalInstance.logoutRedirect());
      }
      if (!noAlert) {
        alertFailed(
          e.response?.data?.message ||
            "Attenzione! Si è verificato un errore. Riprovare più tardi"
        );
      }
      return { ...e.response };
    });
};

// Utilità per gestire i mock dinamicamente (utilizzando il nuovo sistema)
export const mockUtils = {
  // Abilita/disabilita i mock
  setMockEnabled: (enabled: boolean) => {
    MockConfigManager.updateConfig({ enabled });
  },

  // Imposta il delay dei mock
  setMockDelay: (delay: number) => {
    MockConfigManager.setDelay(delay);
  },

  // Abilita errori casuali
  enableRandomErrors: (errorRate = 0.05) => {
    MockConfigManager.enableRandomErrors(errorRate);
  },

  // Disabilita errori casuali
  disableRandomErrors: () => {
    MockConfigManager.disableRandomErrors();
  },

  // Ottiene lo stato della configurazione mock
  getMockConfig: () => MockConfigManager.getConfig(),

  // Ottiene le statistiche sui mock disponibili
  getMockStats: () => {
    const endpoints = Object.keys(FORNITORI_ENDPOINTS);
    return {
      totalEndpoints: endpoints.length,
      mockEndpoints: endpoints,
      config: MockConfigManager.getConfig()
    };
  },
};

export const requests = {
  useFetchJSON,
  dbManag: dbManagApp,
  baseUrlApiApp,
  dbManagControlloTokenFCH,
  mockUtils,
  // Metodi di richiesta con supporto mock
  get: requestAppGet,
  getNoAlert: requestAppGetNoAlert,
  post: requestAppPost,
  postNoAlert: requestAppPostNoAlert,
  put: requestAppPut,
  delete: requestAppDelete,
  // Nuovi metodi per gestire il sistema mock avanzato
  mockConfig: MockConfigManager,
  mockLogger: MockLogger,
};
